import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { AppThunk } from '../store';
import { AdminRole, AdminUserData } from '../types/auth';

interface AuthState {
  user: AdminUserData | null;
  signInLoading: boolean;
  userStateLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  userStateLoading: true,
  signInLoading: false,
  error: null,
}


const service = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSignInLoading(state, action: PayloadAction<boolean>): AuthState {
      return { ...state, signInLoading: action.payload, }
    },
    setUserStateLoading(state, action: PayloadAction<boolean>): AuthState {
      return { ...state, userStateLoading: action.payload, }
    },
    setError(state, action: PayloadAction<string | null>): AuthState {
      return { ...state, error: action.payload, }
    },
    setUserData(state, action: PayloadAction<AdminUserData | null>): AuthState {
      return { ...state, user: action.payload, }
    },
  }
});



const init = (): AppThunk => async (dispatch, getState) => {
  onAuthStateChanged(auth, async (userData) => {
    if (userData) {
      const token = await userData.getIdTokenResult();
      const user: AdminUserData = {
        id: userData.uid,
        name: userData.displayName ?? '',
        email: userData.email ?? '',
        roles: token.claims[ 'roles' ] as AdminRole[]
      };
      dispatch(service.actions.setUserData(user));
    }
    dispatch(service.actions.setUserStateLoading(false));
  })
}

const logout = (): AppThunk => async (dispatch, getState) => {
  await signOut(auth);
  dispatch(service.actions.setUserData(null));
}

const login = (email: string, password: string): AppThunk => async (dispatch, getState) => {
  dispatch(service.actions.setSignInLoading(true));

  try {
    const userCred = await signInWithEmailAndPassword(auth, email, password);
    const token = await userCred.user.getIdTokenResult();
    const user: AdminUserData = {
      id: userCred.user.uid,
      name: userCred.user.displayName ?? '',
      email: userCred.user.email ?? '',
      roles: token.claims[ 'roles' ] as AdminRole[]
    };
    dispatch(service.actions.setUserData(user));
    return true;
  }
  catch (e: any) {
    dispatch(service.actions.setError(e.message));
    console.error(e);
  }
  finally {
    dispatch(service.actions.setSignInLoading(false));
  }

  return false;
}

export const actions = {
  init,
  login,
  logout,
}

export default service.reducer;